import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';

import { makeStyles, IconButton, Typography, Collapse, Theme } from '@material-ui/core';

import DeleteIcon from '@material-ui/icons/CloseOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { MAIN_MAP } from '../../redux/map/Constants';
import {
    setDrawnFeatures,
    setDrawingFeature,
    setShapeFeatureProperty
} from '../../redux/map/Actions';

import Circle from './LegendShapes/Circle';
import Polygon from './LegendShapes/Polygon';
import Line from './LegendShapes/Line';
import Point from './LegendShapes/Point';
import { Feature } from '../../utility/Types/commonTypes';

interface Styles {
    row: React.CSSProperties;
    drowdownButton: React.CSSProperties;
    name: React.CSSProperties;
    autoMarginLeft: React.CSSProperties;
    icon: React.CSSProperties;
    iconMinimised: React.CSSProperties;
    collapseWrapperInner: React.CSSProperties;
    collapseEntered: React.CSSProperties;
    root2: React.CSSProperties;
    label: React.CSSProperties;
    circle: React.CSSProperties;
    fill: React.CSSProperties;
    line: React.CSSProperties;
}

const useStyles = makeStyles<Theme, object, keyof Styles>(() => styles() as Styles);

const propTypes = {};

const defaultProps = {};

function DrawnSketchesLegendItem(): React.ReactElement {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    const drawModule = useSelector((state: any) => state[MAIN_MAP].drawModule);
    const features = useSelector((state: any) => state[MAIN_MAP].drawnFeatures.features);
    const isDrawing = useSelector((state: any) => state[MAIN_MAP].isDrawing);

    function dispatchSetDrawnFeatures(val: any): any {
        dispatch(setDrawnFeatures(val, MAIN_MAP));
    }
    function dispatchSetDrawingFeature(val: any): any {
        dispatch(setDrawingFeature(val, MAIN_MAP));
    }
    function dispatchSetShapeFeatureProperty(
        featureIds: string,
        userProperty: string,
        userPropertyValue: string
    ): any {
        dispatch(setShapeFeatureProperty(featureIds, userProperty, userPropertyValue, MAIN_MAP));
    }

    const generateLegendItemForPanel = (): React.ReactElement => {
        return features.map((item: any) => {
            const drawnFeature = drawModule.getDrawControl().get(item.id);
            if (drawnFeature.properties.radius) {
                return (
                    <Circle
                        key={drawnFeature.id}
                        featureId={drawnFeature.id}
                        fillColour={drawnFeature.properties?.fillColour}
                        strokeColour={drawnFeature.properties?.strokeColour}
                        strokeStyle={drawnFeature.properties?.strokeStyle}
                        name={drawnFeature.properties?.name}
                        locked={drawnFeature.properties?.isLocked}
                    />
                );
            } else if (drawnFeature.geometry.type === 'Polygon') {
                return (
                    <Polygon
                        key={drawnFeature.id}
                        featureId={drawnFeature.id}
                        fillColour={drawnFeature.properties?.fillColour}
                        strokeColour={drawnFeature.properties?.strokeColour}
                        strokeStyle={drawnFeature.properties?.strokeStyle}
                        name={drawnFeature.properties?.name}
                        locked={drawnFeature.properties?.isLocked}
                    />
                );
            } else if (drawnFeature.geometry.type === 'LineString') {
                return (
                    <Line
                        key={drawnFeature.id}
                        featureId={drawnFeature.id}
                        strokeColour={drawnFeature.properties?.strokeColour}
                        strokeStyle={drawnFeature.properties?.strokeStyle}
                        name={drawnFeature.properties?.name}
                        locked={drawnFeature.properties?.isLocked}
                    />
                );
            } else if (drawnFeature.geometry.type === 'Point') {
                return (
                    <Point
                        key={drawnFeature.id}
                        featureId={drawnFeature.id}
                        strokeColour={drawnFeature.properties?.strokeColour}
                        name={drawnFeature.properties?.name}
                        locked={drawnFeature.properties?.isLocked}
                    />
                );
            }
            return null;
        });
    };

    const deleteAllShapes = (): any => {
        drawModule.getDrawControl().deleteAll();
        dispatchSetDrawnFeatures(drawModule.getDrawControl().getAll());
        dispatchSetDrawingFeature(null);
    };

    const toggleAllShapes = (features: Array<Feature>, visibilityString?: string): any => {
        const visibility = visibilityString ?? (isVisible ? 'true' : 'false');
        if (features.length > 0) {
            features.map((item: any) => {
                return dispatchSetShapeFeatureProperty(item.id, 'isVisible', visibility);
            });
        }
    };

    useEffect(() => {
        if (isDrawing) setIsVisible(true);
    }, [isDrawing]);

    useEffect(() => {
        toggleAllShapes(features);
    }, [features.length, isVisible]);

    return (
        <>
            <div className={classes.row}>
                <IconButton
                    classes={{ root: classes.drowdownButton }}
                    size="small"
                    onClick={(): any => setIsCollapsed(!isCollapsed)}>
                    <ArrowDropDownIcon
                        classes={{
                            root: classnames(classes.icon, isCollapsed && classes.iconMinimised)
                        }}
                    />
                </IconButton>
                <Typography classes={{ root: classes.name }} variant="caption" color="textPrimary">
                    Drawn Sketches
                </Typography>
                <IconButton
                    classes={{ root: classes.autoMarginLeft }}
                    size="small"
                    onClick={(): any => setIsVisible(!isVisible)}>
                    {isVisible ? (
                        <VisibilityIcon classes={{ root: classes.icon }} />
                    ) : (
                        <VisibilityOffIcon classes={{ root: classes.icon }} color="disabled" />
                    )}
                </IconButton>
                <IconButton size="small" onClick={(): any => deleteAllShapes()}>
                    <DeleteIcon classes={{ root: classes.icon }} color="disabled" />
                </IconButton>
            </div>
            <Collapse
                in={!isCollapsed}
                classes={{
                    wrapperInner: classes.collapseWrapperInner,
                    entered: classes.collapseEntered
                }}>
                {generateLegendItemForPanel()}
            </Collapse>
        </>
    );
}

const styles = (): any => ({
    row: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px'
    },
    drowdownButton: {
        marginLeft: -6.25
    },
    name: {
        marginTop: 2,
        fontWeight: 500
    },
    autoMarginLeft: {
        marginLeft: 'auto'
    },
    icon: {
        transition: '0.5s',
        width: '0.75em',
        height: '0.75em'
    },
    iconMinimised: {
        transform: 'rotate(-90deg)'
    },
    collapseWrapperInner: {
        padding: '0 10px',
        '&> *:not(:last-child)': {
            marginBottom: 10
        }
    },
    collapseEntered: {
        marginBottom: 10
    },
    root2: {
        display: 'flex',
        alignItems: 'center'
    },
    label: {
        marginLeft: 10,
        fontSize: 10
    },
    circle: {
        borderRadius: '50%',
        flexShrink: 0
    },
    fill: {
        width: 10,
        height: 10,
        flexShrink: 0
    },
    line: {
        minWidth: 20,
        flexShrink: 0
    }
});

DrawnSketchesLegendItem.prototype = propTypes;
DrawnSketchesLegendItem.defaultProps = defaultProps;

export default DrawnSketchesLegendItem;
